import debounce from "lodash/debounce";
import {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import queryString from "query-string";
import useIsMounted from "../use-is-mounted";
import {useDeepCompareEffect} from "react-use";
import axios, {CancelTokenSource} from "axios";
import {deepEqual} from "../../../core/services/utils/utils";


export interface SearchDataSettingsPaginationInfo {
    pageSize: number;
    currentPage: number;
    length: number;
    sizes: number[];
}

export interface SearchDataSettingsOrderBy {
    field: string;
    descending: boolean;
}

export interface SearchDataSettingsFilters {
    [key: string]: any;
}

export type SearchDataSettingsCustomers = any[];
export type SearchDataSettingsVendors = any[];
export type SearchDataSettingsParts = any[];
export type SearchDataSettingsTab = string;

export interface SearchDataSettings {
    paginationInfo?: SearchDataSettingsPaginationInfo;
    orderBy?: SearchDataSettingsOrderBy;
    filters?: SearchDataSettingsFilters;
    customers?: SearchDataSettingsCustomers;
    vendors?: SearchDataSettingsVendors;
    parts?: SearchDataSettingsParts;
    tab?: SearchDataSettingsTab;
    disabled?: boolean;
}

export interface UseSearchResult {
    paginationInfo?: SearchDataSettingsPaginationInfo;
    setPaginationInfo: Dispatch<SetStateAction<SearchDataSettingsPaginationInfo | undefined>>;
    orderBy?: SearchDataSettingsOrderBy;
    setOrderBy?: Dispatch<SetStateAction<SearchDataSettingsOrderBy | undefined>>;
    filters?: SearchDataSettingsFilters;
    setFilters?: Dispatch<SetStateAction<SearchDataSettingsFilters>>;
    customers?: SearchDataSettingsCustomers;
    setCustomers?: Dispatch<SetStateAction<SearchDataSettingsCustomers>>;
    vendors?: SearchDataSettingsVendors;
    setVendors?: Dispatch<SetStateAction<SearchDataSettingsVendors>>;
    parts?: SearchDataSettingsParts;
    setParts?: Dispatch<SetStateAction<SearchDataSettingsParts>>;
    tab?: SearchDataSettingsTab;
    setTab?: Dispatch<SetStateAction<SearchDataSettingsTab | undefined>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    error: any;
    performSearch: (cancelTokenSource?: CancelTokenSource) => Promise<void>;
}


export type UseSearchFunctionArg = Omit<UseSearchResult, 'paginationInfo' | 'loading' | 'error' | 'performSearch'> & {
    paginationInfo?: {
        currentPage: number,
        pageSize: number
    };
    cancelTokenSource?: CancelTokenSource;
};


export type UseSearchFunction<T = any> = (result: UseSearchFunctionArg) => Promise<T>

export interface UseSearchType {
    (searchFunction: UseSearchFunction, initialSettings: SearchDataSettings, useUrlParams?: boolean, debounceDelay?: number): UseSearchResult;
}

/** Marker set when debounced work is cancelled so callers can no-op without treating it as a hard error. */
export const SEARCH_DEBOUNCE_CANCELLED = 'SEARCH_DEBOUNCE_CANCELLED';

/**
 * Throttles an async function in a way that can be awaited.
 * By default, throttle doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * On `cancel()`, pending promises reject with `{[SEARCH_DEBOUNCE_CANCELLED]: true}` so awaiters do not hang forever.
 * @param func async function to throttle calls for.
 * @param wait same function as "lodash/debounce"'s wait parameter.
 *             Call this function at most this often.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
function asyncDebounce<F extends (...args: any[]) => Promise<any>>(func: F, wait?: number) {
    const pendingRejects = new Set<(reason?: any) => void>();
    const debounced = debounce((resolve: (value: any) => void, reject: (reason?: any) => void, args: Parameters<F>) => {
        pendingRejects.delete(reject);
        func(...args).then(resolve).catch(reject);
    }, wait);
    const result = (...args: Parameters<F>): ReturnType<F> =>
        new Promise((resolve, reject) => {
            pendingRejects.add(reject);
            debounced(resolve, reject, args);
        }) as ReturnType<F>;
    result.cancel = () => {
        debounced.cancel();
        const cancelError = Object.assign(new Error('Search debounced call cancelled'), {
            [SEARCH_DEBOUNCE_CANCELLED]: true,
            canceled: true,
        });
        pendingRejects.forEach((reject) => reject(cancelError));
        pendingRejects.clear();
    };
    result.flush = debounced.flush;
    return result;
}

/**
 * Whether an error is from debounce cancel (not a real search failure).
 */
const isSearchDebounceCancelled = (err: unknown): boolean => {
    return !!(err && typeof err === 'object' && ((err as any)[SEARCH_DEBOUNCE_CANCELLED] || (err as any).canceled));
};

/**
 * Custom hook to manage search-related states and synchronize them with the URL.
 *
 * @param searchFunction - The function to execute for performing the search.
 * @param initialSettings - Initial settings for pagination, orderBy, filters, etc.
 * @param useUrlParams  - Whether to synchronize the state with the URL.
 * @param debounceDelay - Delay for debouncing the search function.
 */
const useSearch: UseSearchType = (
    searchFunction,
    initialSettings = {},
    useUrlParams = true,
    debounceDelay = 700,
) => {
    const [paginationInfo, setPaginationInfo] = useState(initialSettings.paginationInfo);
    const [orderBy, setOrderBy] = useState(initialSettings.orderBy);
    const [filters, _setFilters] = useState(initialSettings.filters ?? {});
    const [customers, setCustomers] = useState(initialSettings.customers || []);
    const [vendors, setVendors] = useState(initialSettings.vendors || []);
    const [parts, setParts] = useState(initialSettings.parts || []);
    const [tab, setTab] = useState(initialSettings.tab);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isInitializedFromUrl, setIsInitializedFromUrl] = useState(false);

    const location = useLocation();
    const history = useHistory();
    const isMounted = useIsMounted();

    const previouslyDisabledRef = useRef(initialSettings.disabled);

    const initializedFromUrlRef = useRef(false);
    const userTriggeredChangeRef = useRef(0);

    const setFilters = useCallback<Dispatch<SetStateAction<SearchDataSettingsFilters>>>((state) => {
        _setFilters((prevState) => {
            let newState: SearchDataSettingsFilters;
            if (typeof state === 'function') {
                newState = state(prevState)
            } else {
                newState = state
            }
            if (deepEqual(prevState, newState)) {
                return prevState;
            }
            return newState;
        });
    }, [])

    /**
     * Checks if the state is non-null, non-undefined, and not an empty array.
     */
    const isValidState = useCallback((state: any) => {
        return state !== null && state !== undefined && !(Array.isArray(state) && state.length === 0);
    }, []);

    /**
     * Serializes state for URL synchronization
     */
    const serializeState = useCallback((state: any) => {
        if (typeof state === 'string')
            return state;
        return JSON.stringify(state);
    }, []);

    /**
     * Deserializes state from URL parameters
     */
    const deserializeState = useCallback((param: string[] | string | null, defaultValue: any): any => {
        if (Array.isArray(param)) {
            return param.map(p => deserializeState(p, defaultValue));
        }
        try {
            const parsed = JSON.parse(param as any);
            return isValidState(parsed) ? parsed : defaultValue;
        } catch (e) {
            return defaultValue;
        }
    }, [isValidState]);


    /**
     * Synchronize state with URL.
     */
    const syncWithUrl = useCallback(() => {
        if (!useUrlParams || !isInitializedFromUrl) return;

        const newQuery: any = {};
        if (isValidState(tab)) newQuery.tab = serializeState(tab);
        if (isValidState(paginationInfo)) newQuery.paginationInfo = serializeState(paginationInfo);
        if (isValidState(orderBy)) newQuery.orderBy = serializeState(orderBy);
        if (isValidState(filters)) newQuery.filters = serializeState(filters);
        if (isValidState(customers)) newQuery.customers = serializeState(customers);
        if (isValidState(vendors)) newQuery.vendors = serializeState(vendors);
        if (isValidState(parts)) newQuery.parts = serializeState(parts);
        const queryStringified = queryString.stringify(newQuery, {arrayFormat: 'bracket'});
        if (location.search !== `?${queryStringified}`) {
            history.replace({pathname: location.pathname, search: queryStringified, hash: location.hash});
        }
    }, [tab, paginationInfo, orderBy, filters, customers, vendors, parts, isInitializedFromUrl, history, location, useUrlParams, isValidState, serializeState]);

    /**
     * This is the debounced version of the syncWithUrl function.
     */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    const syncWithUrlDebounced = useCallback(debounce(syncWithUrl, 300), [syncWithUrl]);

    /**
     * Synchronize state with URL when the state changes.
     */
    useEffect(() => {
        if (useUrlParams) {
            syncWithUrlDebounced();
            return () => syncWithUrlDebounced.cancel();
        }
    }, [syncWithUrlDebounced, useUrlParams]);

    /**
     * Initializes the state from URL parameters or from provided initial settings
     */
    useDeepCompareEffect(() => {
        if (useUrlParams && !initializedFromUrlRef.current) {
            // Deserialize URL parameters and set state
            const params = queryString.parse(window.location.search, {arrayFormat: 'bracket',});
            setPaginationInfo(deserializeState(params.paginationInfo, initialSettings.paginationInfo));
            setOrderBy(deserializeState(params.orderBy, initialSettings.orderBy));
            setFilters(deserializeState(params.filters, initialSettings.filters ?? {}));
            setCustomers(deserializeState(params.customers, initialSettings.customers || []));
            setVendors(deserializeState(params.vendors, initialSettings.vendors || []));
            setParts(deserializeState(params.parts, initialSettings.parts || []));
            setTab(deserializeState(params.tab, initialSettings.tab));

            // Mark initialization complete and reset user-triggered change flag
            initializedFromUrlRef.current = true;
            userTriggeredChangeRef.current = 0;
        } else if (!useUrlParams) {
            initializedFromUrlRef.current = true;
            userTriggeredChangeRef.current = 2;
        }
        setIsInitializedFromUrl(true);
    }, [deserializeState, initialSettings.customers, initialSettings.filters, initialSettings.orderBy, initialSettings.paginationInfo, initialSettings.parts, initialSettings.tab, initialSettings.vendors, useUrlParams]);

    /**
     * Tracks user-triggered changes
     */
    useDeepCompareEffect(() => {
        if (isInitializedFromUrl && initializedFromUrlRef.current) {
            userTriggeredChangeRef.current = Math.min(userTriggeredChangeRef.current + 1, 2);
        }
    }, [orderBy, filters, customers, vendors, parts, tab, isInitializedFromUrl]);

    /**
     * Resets pagination when the filters or other search parameters change
     */
    useDeepCompareEffect(() => {
        if (isInitializedFromUrl && initializedFromUrlRef.current && userTriggeredChangeRef.current === 2) {
            setPaginationInfo(initialSettings.paginationInfo);
        }
    }, [orderBy, filters, customers, vendors, parts, tab, initialSettings.paginationInfo, isInitializedFromUrl]);

    const hasPaginationInfo = useMemo<boolean>(() => !!paginationInfo, [paginationInfo]);
    const result = useMemo<Omit<UseSearchFunctionArg, 'cancelTokenSource'>>(() => ({
        paginationInfo: hasPaginationInfo ? {
            currentPage: paginationInfo?.currentPage as number,
            pageSize: paginationInfo?.pageSize as number
        } : undefined,
        setPaginationInfo,
        orderBy, setOrderBy,
        filters, setFilters,
        customers, setCustomers,
        vendors, setVendors,
        parts, setParts,
        tab, setTab,
        setLoading,
    }), [hasPaginationInfo, paginationInfo?.currentPage, paginationInfo?.pageSize, orderBy, filters, setFilters, customers, vendors, parts, tab]);

    /**
     * The debounced version of the search function.
     */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearchFunction = useCallback(asyncDebounce(searchFunction, debounceDelay), [searchFunction, debounceDelay]);

    /**
     * Performs the search and updates the state accordingly.
     */
    const performSearch = useCallback(async (cancelTokenSource?: CancelTokenSource) => {
        setLoading(true);
        try {
            await debouncedSearchFunction({...result, cancelTokenSource})
        } catch (err) {
            if (!isSearchDebounceCancelled(err)) {
                console.error('use search error:\n', err);
                setError(err as any);
            }
        }
        if (!isMounted() || !!cancelTokenSource?.token?.reason)
            return;
        setLoading(false);
    }, [debouncedSearchFunction, isMounted, result]);

    /**
     * Triggers the search when relevant states change.
     */
    useEffect(() => {
        if (!isInitializedFromUrl || initialSettings?.disabled)
            return () => {
                previouslyDisabledRef.current = initialSettings.disabled;
            }

        const cancelTokenSource = axios.CancelToken.source();
        performSearch(cancelTokenSource).then();
        if (previouslyDisabledRef.current && !initialSettings.disabled) {
            debouncedSearchFunction.flush();
        } else if (initialSettings.disabled && !previouslyDisabledRef.current) {
            setLoading(false);
        }
        return () => {
            previouslyDisabledRef.current = initialSettings.disabled;
            cancelTokenSource.cancel();
            debouncedSearchFunction.cancel()
        };
    }, [debouncedSearchFunction, isInitializedFromUrl, performSearch, initialSettings.disabled]);

    /**
     * Cancels the debounced search function when the component is unmounted.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => debouncedSearchFunction.cancel(), []);

    return useMemo(() => ({
        ...result,
        loading,
        error,
        performSearch,
        paginationInfo: hasPaginationInfo
            ? {...result.paginationInfo, length: paginationInfo?.length, sizes: paginationInfo?.sizes} as SearchDataSettingsPaginationInfo
            : undefined,
    }), [result, loading, error, hasPaginationInfo, paginationInfo?.length, paginationInfo?.sizes, performSearch]);
};

export default useSearch;
